import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
} from '@mui/material';
import {
  useDocumentationBrowseDialog,
  withDocumentationBrowseDialog,
} from 'components/common/withDocumentationBrowseDialog';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import {
  MemoStatefulAutocompleteWithText,
  MemoStatefulSingleSelect,
} from 'components/dataCreationForms/withInputForm/statefulInputsWrapper';
import { useLevels } from 'store/level';
import { getPrimaryMap } from 'shared/domain/level/types/model';
import { DocumentationBrowseDialog } from 'components/documentation/DocumentationBrowseDialog';
import { ButtonContained } from 'components/general';
import {
  FormFieldProps,
  SingleChoiceFormFieldProps,
} from 'components/inspection/Form/types';
import { useCreateStore } from 'hooks/createStore';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import {
  ReactElement,
  memo,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { FormattedMessage, formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { toLabelledEntity } from 'helpers/misc';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import { useSites } from 'store/site';
import { LabelledEntity } from 'shared/types/commonView';
import { useDirectoryPath } from 'hooks/documentation/useDirectoryPath';
import { normalizeDirectoryId } from 'shared/domain/directory/directoryIds';
import { PreselectedMapDisplayData } from 'components/dataCreationForms/level/types';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { ProjectSetupTranslationKeys } from 'intl/keys';
import { FieldTranslationKeys } from 'intl/keys';

interface LevelFormProps {
  levelId?: string;
  preselectedMapDisplay?: PreselectedMapDisplayData;
}

type LevelFormFC = (props: LevelFormProps) => ReactElement;

function _LevelForm({ levelId, preselectedMapDisplay }: LevelFormProps): ReactElement {
  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const { items: levels, loading } = useLevels();
  const { openDialog } = useDocumentationBrowseDialog();
  const selectedStore = useCreateStore<string | undefined>(undefined);
  const intl = useIntl();
  const [documentation, setDocumentation] = useState<
    { name: string } | undefined
  >(undefined);
  const [initialSelect, setInitialSelect] = useState<string | undefined>(
    undefined
  );
  const [initialDirectory, setInitialDirectory] = useState<string>('');

  // Folder of the selected map - local, so picking a map never changes the
  // documentation grid's current directory.
  const [mapDirectoryId, setMapDirectoryId] = useState<string | undefined>(undefined);
  const { path: mapPath, isLoading: isLoadingMapPath } = useDirectoryPath(mapDirectoryId);

  const isLoading = levelId && (loading || isLoadingMapPath || loadingSites);

  interface SetableSiteFields {
    site: LabelledEntity;
  }

  const siteField: SingleChoiceFormFieldProps<SetableSiteFields> = useMemo(
    () => ({
      formKey: 'site',
      required: true,
      labelId: FieldTranslationKeys.site,
      fieldName: 'singleselect-site',
      available: sites.filter(keepUndeleted).map(toLabelledEntity),
      autoSelect: 'asSingleSelect',
      entityName: EditableStandardEntityName.location,
      reserveSpaceForHelperText: levelId ? false : true,
      dense: true,
      'data-qa': 'level_site_field',
      issueParams: null,
      disabled: levelId ? true : false,
    }),
    [sites, levelId]
  );

  interface SetableLevelFields {
    label: string;
  }

  const levelField: FormFieldProps<SetableLevelFields> = {
    formKey: `label`,
    required: true,
    labelId: FieldTranslationKeys.level,
    fieldName: `level-label`,
    localStorageKey: 'level-label',
    dense: true,
    reserveSpaceForHelperText: true,
    'data-qa': 'level_label_field',
    issueParams: null,
  };

  useEffect((): void => {
    const syncMapFromDocumentation = async (
      documentationId: string
    ): Promise<void> => {
      const documentation = await documentationClient.getOneById(documentationId);
      if (!documentation) return;
      const parentDirectoryId = normalizeDirectoryId(documentation.parentId);
      setMapDirectoryId(parentDirectoryId);
      setInitialDirectory(parentDirectoryId);
      setDocumentation({ name: documentation.name });
      const selection = `${documentation.localId}_FILE`;
      selectedStore.set(selection);
      setInitialSelect(selection);
    };

    if (preselectedMapDisplay) {
      setDocumentation({ name: preselectedMapDisplay.name });
      const selection = `${preselectedMapDisplay.localId}_FILE`;
      selectedStore.set(selection);
      setInitialSelect(selection);

      if (preselectedMapDisplay.documentationId) {
        syncMapFromDocumentation(preselectedMapDisplay.documentationId);
      }
      return;
    }

    if (!levelId) return;

    const levelModel = levels.find((l) => areObjectIdsEqual(l._id, levelId));
    const primaryMap = levelModel ? getPrimaryMap(levelModel) : undefined;
    const documentationId = primaryMap?.documentationId;
    if (documentationId) {
      syncMapFromDocumentation(documentationId);
    }
  }, [
    levels,
    levelId,
    preselectedMapDisplay,
    selectedStore,
  ]);

  return (
    <Box>
      <MemoStatefulSingleSelect {...siteField} />
      <MemoStatefulAutocompleteWithText {...levelField} />
      <TextField
        multiline
        maxRows={2}
        variant='outlined'
        placeholder={formatTranslation(intl, ProjectSetupTranslationKeys.selectMap,)}
        value={
          !documentation || isLoading
            ? ''
            : clampString(
                90,
                [...mapPath, documentation].map((v) => v.name).join('/')
              )
        }
        InputProps={{
          readOnly: true,
          style: {
            //@ts-ignore
            '& textarea': {
              overflowY: 'hidden',
            },
          },
          // className: classes.mapInput,
          startAdornment: isLoading ? (
            <CircularProgress
              size={13}
              thickness={4}
              disableShrink
              color='secondary'
            />
          ) : null,
          endAdornment: (
            <InputAdornment position='end'>
              <ButtonContained onClick={openDialog} disabled={loading}>
                <FormattedMessage id={CommonTranslationKeys.select} />
              </ButtonContained>
            </InputAdornment>
          ),
        }}
      />
      <DocumentationBrowseDialog
        formKey='map'
        setValuesContext={useInputForm}
        setFilename={(name) => setDocumentation({ name })}
        selectedStore={selectedStore}
        title={mapPath[mapPath.length - 1]}
        initialSelect={initialSelect}
        initialDirectory={initialDirectory}
        onDirectoryChange={setMapDirectoryId}
      />
    </Box>
  );
}

export const LevelForm: LevelFormFC = withDocumentationBrowseDialog(_LevelForm);

function clampString(length: number, text: string): string {
  if (text.length > length) {
    return `/... ${text.slice(text.length - length, text.length)}`;
  }
  return text;
}

export const MemoLevelForm = memo(LevelForm);
